
import { forwardRef, useCallback, useLayoutEffect, useRef, useState } from 'react';
import type { HTMLAttributes, RefObject } from 'react';
import { createPortal } from 'react-dom';
import { cx } from '@client/shared/lib/cx.ts';
import { setRef } from '@client/shared/lib/set-ref.ts';
import styles from './AnchoredCard.module.css';

export type CardAnchor =
  | { point: { x: number; y: number } | null }
  | { ref: RefObject<HTMLElement | null> };

/** Which side of the anchor the card sits on (overrides the auto-flip). */
export type CardPlacement = 'top' | 'bottom' | 'left' | 'right';

export interface AnchoredCardProps extends HTMLAttributes<HTMLDivElement> {
  anchor: CardAnchor;
  className?: string;
  gap?: number;
  align?: 'start' | 'end';
  cardWidth?: number;
  flipRoom?: number;
  flipUp?: number;
  /** Pin the card to a side of the anchor instead of auto-flipping below/above. */
  placement?: CardPlacement;
  /**
   * Tooltip mode: draw a pointer toward the anchor, position off the card's measured
   * size (so the pointer touches), and track scroll/resize. `align='start'` only.
   * Without it the card is a transient popover: positioned once per anchor change
   * from the `cardWidth`/`flipUp` estimates.
   */
  arrow?: boolean;
}

const ARROW_INSET = 16;

type ArrowDir = 'up' | 'down' | 'left' | 'right';

const ARROW_CLASS: Record<ArrowDir, string | undefined> = {
  up: styles.arrowUp,
  down: styles.arrowDown,
  left: styles.arrowLeft,
  right: styles.arrowRight,
};

interface Pos {
  top: number;
  left?: number;
  right?: number;
  arrowDir?: ArrowDir;
  arrowX?: number;
  arrowY?: number;
}

function anchorEdges(
  anchor: CardAnchor,
  cardWidth: number,
): { top: number; bottom: number; left: number; right: number } | null {
  if ('point' in anchor) {
    if (!anchor.point) return null;
    const { x, y } = anchor.point;
    return { top: y, bottom: y, left: x - cardWidth / 2, right: x + cardWidth / 2 };
  }
  const el = anchor.ref.current;
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return { top: r.top, bottom: r.bottom, left: r.left, right: r.right };
}

const clamp = (v: number, lo: number, hi: number) => Math.min(Math.max(v, lo), Math.max(lo, hi));

const samePos = (a: Pos | null, b: Pos | null) =>
  a === b ||
  (a != null &&
    b != null &&
    a.top === b.top &&
    a.left === b.left &&
    a.right === b.right &&
    a.arrowDir === b.arrowDir &&
    a.arrowX === b.arrowX &&
    a.arrowY === b.arrowY);

export const AnchoredCard = forwardRef<HTMLDivElement, AnchoredCardProps>(function AnchoredCard(
  {
    anchor,
    className,
    gap = 8,
    align = 'start',
    cardWidth = 300,
    flipRoom = 220,
    flipUp = 220,
    placement,
    arrow = false,
    children,
    ...rest
  },
  ref,
) {
  const [pos, setPos] = useState<Pos | null>(null);
  const innerRef = useRef<HTMLDivElement | null>(null);

  const pointKey = 'point' in anchor ? anchor.point : null;
  const refKey = 'ref' in anchor ? anchor.ref : null;
  useLayoutEffect(() => {
    const apply = (next: Pos | null) => setPos((prev) => (samePos(prev, next) ? prev : next));
    const compute = () => {
      const edges = anchorEdges(anchor, cardWidth);
      if (!edges) {
        apply(null);
        return;
      }
      // Tooltip (arrow) mode measures the real card so a flipped/side card sits flush
      // against the anchor; the popover path keeps its cheap estimates so its placement
      // never shifts.
      const cardH = arrow ? innerRef.current?.offsetHeight : undefined;
      const cardW = (arrow ? innerRef.current?.offsetWidth : undefined) ?? cardWidth;

      if (placement === 'left' || placement === 'right') {
        const h = cardH ?? 0;
        const centerY = (edges.top + edges.bottom) / 2;
        const top = clamp(centerY - h / 2, gap, window.innerHeight - h - gap);
        const arrowY = clamp(centerY - top, ARROW_INSET, h - ARROW_INSET);
        // Keep the card on-screen even when the anchor sits near the same edge it
        // points from (e.g. a left-placed card on a narrow viewport).
        const maxLeft = window.innerWidth - cardW - gap;
        if (placement === 'left') {
          apply({ top, right: clamp(window.innerWidth - edges.left + gap, gap, maxLeft), arrowDir: 'right', arrowY });
        } else {
          apply({ top, left: clamp(edges.right + gap, gap, maxLeft), arrowDir: 'left', arrowY });
        }
        return;
      }

      const flip =
        placement === 'top' || (placement == null && window.innerHeight - edges.bottom < flipRoom);
      const top = flip
        ? cardH != null
          ? Math.max(gap, edges.top - cardH - gap)
          : Math.max(gap, edges.top - flipUp)
        : edges.bottom + gap;
      if (align === 'end') {
        apply({ top, right: window.innerWidth - edges.right });
      } else {
        const left = clamp(edges.left, gap, window.innerWidth - cardW - gap);
        const centerX = (edges.left + edges.right) / 2;
        const arrowX = clamp(centerX - left, ARROW_INSET, cardW - ARROW_INSET);
        apply({ top, left, arrowDir: flip ? 'down' : 'up', arrowX });
      }
    };
    compute();
    if (!arrow) return;
    window.addEventListener('scroll', compute, true);
    window.addEventListener('resize', compute);
    return () => {
      window.removeEventListener('scroll', compute, true);
      window.removeEventListener('resize', compute);
    };
  }, [pointKey, refKey, align, gap, cardWidth, flipRoom, flipUp, placement, arrow]);

  const setRefs = useCallback(
    (node: HTMLDivElement | null) => {
      innerRef.current = node;
      setRef(ref, node);
    },
    [ref],
  );

  let style: HTMLAttributes<HTMLDivElement>['style'];
  if (pos && align === 'end') {
    style = { top: pos.top, '--anchor-right': `${pos.right}px` } as Record<string, string | number>;
  } else if (pos) {
    style = { top: pos.top, left: pos.left, right: pos.right };
  } else if (refKey) {
    style = { visibility: 'hidden' };
  }

  return createPortal(
    <div
      {...rest}
      ref={setRefs}
      className={cx(styles.anchoredCard, align === 'end' && styles.alignEnd, className)}
      style={style}
    >
      {arrow && pos?.arrowDir && (
        <span
          aria-hidden
          className={cx(styles.arrow, ARROW_CLASS[pos.arrowDir])}
          style={pos.arrowY !== undefined ? { top: pos.arrowY } : { left: pos.arrowX }}
        />
      )}
      {children}
    </div>,
    document.body,
  );
});
